class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerSummaries = this.querySelectorAll('[id^="Details-"] summary');
    cartDrawerSummaries.forEach((summary) => {
      if (!summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
    });
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerSummary) {
    cartDrawerSummary.setAttribute('role', 'button');
    cartDrawerSummary.setAttribute('aria-expanded', 'false');

    if (cartDrawerSummary.nextElementSibling && cartDrawerSummary.nextElementSibling.getAttribute('id')) {
      cartDrawerSummary.setAttribute('aria-controls', cartDrawerSummary.nextElementSibling.id);
    }

    cartDrawerSummary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerSummary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const isEmpty = parsedState.item_count === 0;
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      document.dispatchEvent(new CustomEvent('cart-drawer:updated', { detail: { parsedState } }));
      if (typeof window.initCartDrawerRecommendations === 'function') {
        window.initCartDrawerRecommendations();
        setTimeout(window.initCartDrawerRecommendations, 50);
        setTimeout(window.initCartDrawerRecommendations, 200);
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function initCartDrawerRecommendations() {
  if (typeof jQuery === 'undefined' || typeof jQuery.fn.slick === 'undefined') {
    setTimeout(initCartDrawerRecommendations, 100);
    return;
  }

  const $sliders = jQuery('.cart-drawer_you_may_also_like-slider');
  if (!$sliders.length) return;

  $sliders.each(function () {
    const $slider = jQuery(this);

    if ($slider.hasClass('slick-initialized')) {
      try {
        $slider.slick('setPosition');
        return;
      } catch (e) {
        try {
          $slider.slick('unslick');
        } catch (err) {}
      }
    }

    const slideCount = $slider.find('.cart-drawer_you_may_also_like-slide').length;
    if (slideCount === 0) return;

    $slider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: slideCount > 1,
      dots: false,
      infinite: false,
      adaptiveHeight: true,
      prevArrow: '<button type="button" class="slick-prev" aria-label="Previous">‹</button>',
      nextArrow: '<button type="button" class="slick-next" aria-label="Next">›</button>',
    });
  });
}

window.initCartDrawerRecommendations = initCartDrawerRecommendations;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerRecommendations);
} else {
  initCartDrawerRecommendations();
}

document.addEventListener('cart-drawer:opened', function () {
  setTimeout(initCartDrawerRecommendations, 50);
  setTimeout(initCartDrawerRecommendations, 200);
});

document.addEventListener('cart-drawer:updated', function () {
  setTimeout(initCartDrawerRecommendations, 50);
  setTimeout(initCartDrawerRecommendations, 200);
});

